import React, { useState, useEffect, useRef } from 'react';
import {
    Text,
    Heading,
    Badge,
    Icon,
    Button,
    Container
} from './ui/index.jsx';

// AnalysisDisplay React component for rendering AI analysis results
// Converted from vanilla JS to React + Vite
const AnalysisDisplay = ({ data, research, validation, platform, originalIdea, onError }) => {
    const [isV2Format, setIsV2Format] = useState(true);
    const [normalizedData, setNormalizedData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Detect data format and normalize data on mount or data change
    useEffect(() => {
        if (!data) {
            setError('No data provided to AnalysisDisplay');
            setIsLoading(false);
            return;
        }


        try {
            const detectedFormat = detectDataFormat(data);
            setIsV2Format(detectedFormat);

            const normalized = getNormalizedData(data, detectedFormat);
            setNormalizedData(normalized);
            setIsLoading(false);

        } catch (err) {
            if (import.meta.env.DEV) {
            }
            setError('Failed to process analysis results');
            setIsLoading(false);
            if (onError) onError(err);
        }
    }, [data, research, validation, platform, originalIdea, onError]);

    // V2 data format detection (V1 compatibility removed)
    const detectDataFormat = (data) => {
        return true;
    };

    // Extract and display HLD data from Step 2 (Overview Tab = Refined HLD after validation)
    const extractFromHLDData = (data) => {

        if (data.validation && data.refinedHLD && data.riskAssessment) {
            const refinedHLD = data.refinedHLD;
            const validation = data.validation;
            const risks = data.riskAssessment;

            return {
                // Enhanced with validation context
                originalIdea: `Validated Architecture${validation.isValid ? ' ✅' : ' ⚠️ Needs Improvements'}`,
                coreFeatures: refinedHLD.businessRequirements?.coreFeatures || [],

                // Complete tech stack from refined HLD
                techStack: {
                    frontend: `${refinedHLD.technicalArchitecture?.frontendArchitecture?.framework || 'React/Next.js'} (${refinedHLD.technicalArchitecture?.frontendArchitecture?.type || 'SPA'})`,
                    backend: `${refinedHLD.technicalArchitecture?.backendArchitecture?.framework || 'Node.js/Express'} (${refinedHLD.technicalArchitecture?.backendArchitecture?.type || 'REST'})`,
                    database: `${refinedHLD.technicalArchitecture?.databaseArchitecture?.provider || 'PostgreSQL'} (${refinedHLD.technicalArchitecture?.databaseArchitecture?.type || 'Relational'})`,
                    authentication: `${refinedHLD.technicalArchitecture?.securityArchitecture?.authentication || 'JWT'} + ${refinedHLD.technicalArchitecture?.securityArchitecture?.authorization || 'RBAC'}`,
                    deployment: refinedHLD.technicalArchitecture?.integrationArchitecture?.deploymentServices?.[0] || 'Vercel/Netlify'
                },

                targetAudience: refinedHLD.projectMetadata?.targetAudience || refinedHLD.businessRequirements?.userTypes?.join(', ') || 'General users',

                // Risk-based challenges (this is key for Step 2)
                challenges: [
                    ...(risks.technicalRisks?.map(risk => `🔧 ${risk.risk} (${risk.probability}/${risk.impact})`) || []),
                    ...(risks.businessRisks?.map(risk => `📊 ${risk.risk} (${risk.probability}/${risk.impact})`) || []),
                    ...(risks.timelineRisks?.map(risk => `⏱️ ${risk.risk} (${risk.probability}/${risk.impact})`) || [])
                ],

                // Validation-enhanced development phases
                developmentPhases: [{
                    title: 'Validated Architecture Plan',
                    description: `${validation.isValid ? 'Architecture validated successfully' : 'Architecture needs improvements'} - Risk assessment completed`,
                    duration: refinedHLD.projectMetadata?.timeline || refinedHLD.developmentStrategy?.timeline || 'Variable',
                    tasks: [
                        ...(refinedHLD.businessRequirements?.coreFeatures || []),
                        ...(validation.improvements || [])
                    ]
                }],

                // Service recommendations from validation
                serviceRecommendations: {
                    hosting: refinedHLD.technicalArchitecture?.integrationArchitecture?.deploymentServices || [],
                    database: [refinedHLD.technicalArchitecture?.databaseArchitecture?.provider],
                    authentication: [refinedHLD.technicalArchitecture?.securityArchitecture?.authentication],
                    monitoring: refinedHLD.technicalArchitecture?.performanceArchitecture?.monitoringTools || [],
                    validation: validation.recommendations ? [validation.recommendations] : []
                },

                // Enhanced research summary with validation results
                researchSummary: {
                    validationStatus: validation.isValid ? 'Valid' : 'Needs Review',
                    validationResults: {
                        researchQuality: validation.validationResults?.researchQuality || 'good',
                        techStackCoherence: validation.validationResults?.techStackCoherence || 'good',
                        requirementsFit: validation.validationResults?.requirementsFit || 'good',
                        goalAlignment: validation.validationResults?.goalAlignment || 'good',
                        complexityBalance: validation.validationResults?.complexityBalance || 'good',
                        scalabilityPath: validation.validationResults?.scalabilityPath || 'good',
                        securityAdequacy: validation.validationResults?.securityAdequacy || 'good'
                    },
                    improvements: validation.improvements?.length || 0,
                    concerns: validation.concerns?.length || 0,
                    riskLevel: risks.technicalRisks?.[0]?.impact || 'low'
                },

                researchEnhanced: true,
                projectType: refinedHLD.projectMetadata?.projectType || 'web-app',
                category: refinedHLD.projectMetadata?.targetAudience?.toLowerCase()?.includes('business') ? 'business' : 'productivity',
                complexity: refinedHLD.projectMetadata?.complexity || 'medium',
                estimatedTimeframe: refinedHLD.projectMetadata?.timeline || refinedHLD.developmentStrategy?.timeline || '1-2 months',
                confidence: data.confidence || 0.85,
                reasoning: data.enhancementJustification || validation.recommendations || 'Architecture validated and refined based on comprehensive analysis',

                // Enhanced metadata for Step 2
                metadata: {
                    step: 2,
                    validated: true,
                    validationScore: data.confidenceFactors?.validationAccuracy || 'High',
                    riskLevel: risks.technicalRisks?.[0]?.impact || 'low',
                    architecture: refinedHLD.projectMetadata?.architecture,
                    budget: refinedHLD.projectMetadata?.budget,
                    methodology: refinedHLD.developmentStrategy?.methodology || 'agile'
                },

                detectedRequirements: [
                    ...(refinedHLD.businessRequirements?.dataRequirements || []),
                    ...(refinedHLD.businessRequirements?.integrationNeeds || []),
                    ...(refinedHLD.businessRequirements?.workflowRequirements || [])
                ],

                budgetAnalysis: {
                    tier: refinedHLD.projectMetadata?.budget,
                    costProjections: data.researchFindings?.serviceAnalysis?.costProjections || 'Optimized for budget tier',
                    services: refinedHLD.technicalArchitecture?.integrationArchitecture?.deploymentServices
                },

                // Validation-specific analysis
                freeTierAnalysis: {
                    viability: data.confidenceFactors?.freeTierViability || 'Good',
                    services: refinedHLD.technicalArchitecture?.integrationArchitecture?.serviceSelection?.platformRequirements || 'Compatible with free tiers'
                }
            };
        }

        if (data.researchSources && data.highLevelDesign && data.researchFindings) {
            const hld = data.highLevelDesign;
            return {
                originalIdea: '🔬 Research-Based Architecture (Not yet validated)',
                coreFeatures: hld.businessRequirements?.coreFeatures || [],
                techStack: {
                    frontend: hld.technicalArchitecture?.frontendArchitecture?.framework || 'React/Next.js',
                    backend: hld.technicalArchitecture?.backendArchitecture?.framework || 'Node.js/Express',
                    database: hld.technicalArchitecture?.databaseArchitecture?.provider || 'PostgreSQL',
                    authentication: hld.technicalArchitecture?.securityArchitecture?.authentication || 'JWT',
                    deployment: hld.technicalArchitecture?.integrationArchitecture?.deploymentServices?.[0] || 'Vercel/Netlify'
                },
                targetAudience: hld.projectMetadata?.targetAudience || hld.businessRequirements?.userTypes?.join(', ') || 'General users',
                challenges: data.researchFindings?.riskAssessment || [],
                developmentPhases: [{
                    title: 'Research & Design',
                    description: 'Architecture and technology selection completed - Awaiting validation',
                    duration: hld.projectMetadata?.timeline || 'Variable',
                    tasks: hld.businessRequirements?.coreFeatures || []
                }],
                serviceRecommendations: {
                    hosting: hld.technicalArchitecture?.integrationArchitecture?.deploymentServices || [],
                    database: [hld.technicalArchitecture?.databaseArchitecture?.provider],
                    authentication: [hld.technicalArchitecture?.securityArchitecture?.authentication],
                    monitoring: hld.technicalArchitecture?.performanceArchitecture?.monitoringTools || []
                },
                researchSummary: {
                    sources: data.researchSources?.length || 0,
                    findings: data.researchFindings?.trendAnalysis || 'Comprehensive analysis completed',
                    confidence: data.confidenceFactors?.overallConfidence || 'High'
                },
                researchEnhanced: true,
                projectType: hld.projectMetadata?.projectType || 'web-app',
                category: 'productivity',
                complexity: hld.projectMetadata?.complexity || 'medium',
                estimatedTimeframe: hld.projectMetadata?.timeline || '1-2 months',
                confidence: data.confidence || 0.8,
                reasoning: data.researchFindings?.trendAnalysis || hld.businessRequirements?.workflowRequirements?.join('. ') || '',
                metadata: {
                    step: 1,
                    validated: false,
                    architecture: hld.projectMetadata?.architecture,
                    budget: hld.projectMetadata?.budget,
                    researchQuality: data.confidenceFactors?.researchQuality
                },
                detectedRequirements: hld.businessRequirements?.dataRequirements || [],
                budgetAnalysis: {
                    tier: hld.projectMetadata?.budget,
                    services: hld.technicalArchitecture?.integrationArchitecture?.deploymentServices
                },
                freeTierAnalysis: data.researchFindings?.serviceAnalysis || null
            };
        }

        return null;
    };

    const getNormalizedData = (data) => {

        const hldData = extractFromHLDData(data);
        if (hldData) {
            return hldData;
        }


        if (research && typeof research === 'object') {

            const finalStack = data?.finalTechStack
                || validation?.improvedArchitecture?.techStack
                || validation?.refinedArchitecture?.techStack
                || null;
            const techStack = finalStack || research.techStack || {};


            return {
                originalIdea: originalIdea || data?.originalIdea || 'Your app idea',
                coreFeatures: improvedArch.coreFeatures || research.coreFeatures || data?.coreFeatures || [],

                // Use REAL tech stack (validated/final preferred; research only if validated not present)
                techStack: {
                    frontend: techStack.frontend || 'Not specified',
                    backend: techStack.backend || 'Not specified',
                    database: techStack.database || 'Not specified',
                    authentication: techStack.authentication || 'Not specified',
                    deployment: techStack.deployment || 'Not specified',
                    cdn: techStack.cdn || 'Not specified',
                    additionalServices: techStack.additionalServices || []
                },

                targetAudience: data?.targetAudience || 'General users',
                challenges: data?.challenges || [],

                // Use research timeframe and complexity
                developmentPhases: data?.developmentPhases || [{
                    title: 'Implementation Plan',
                    description: `Development using ${platform} with researched tech stack`,
                    duration: research.estimatedTimeframe || '2-4 weeks',
                    tasks: improvedArch.coreFeatures || research.coreFeatures || []
                }],

                // Show validation improvements as service recommendations
                serviceRecommendations: validation ? {
                    improvements: validation.validation?.improvements || [],
                    alternatives: validation.validation?.betterFreeAlternatives || [],
                    recommendations: validation.validation?.recommendations || ''
                } : (data?.serviceRecommendations || {}),

                researchSummary: {
                    totalRequirements: research.researchSources?.awesomeListsConsulted?.length || 0,
                    budgetPreference: research.estimatedCost?.monthly === '$0' ? 'free-tier focused' : 'budget-conscious',
                    topRecommendation: techStack.frontend || 'Not specified',
                    researchSources: research.researchSources || {}
                },
                researchEnhanced: true,
                projectType: research.projectType || 'web-app',
                category: 'productivity',
                complexity: research.complexity || 'medium',
                estimatedTimeframe: research.estimatedTimeframe || '2-4 weeks',
                confidence: 0.9, // High confidence since we have research data
                reasoning: research.reasoning || validation?.improvedArchitecture?.reasoning || '',
                metadata: {
                    platform: platform,
                    hasResearchData: true,
                    hasValidationData: !!validation
                },
                detectedRequirements: [],
                budgetAnalysis: {
                    preference: research.estimatedCost?.monthly === '$0' ? 'free-tier' : 'budget-conscious'
                },
                freeTierAnalysis: {
                    totalFreeServices: research.researchedServices?.totalFreeServices || 0,
                    monthlyValue: research.estimatedCost?.monthly || '$0'
                }
            };
        }

        // If data.success exists, extract from the actual analysis response structure
        if (data && typeof data === 'object') {
            // Handle the cursorBlueprint structure if it exists
            const blueprint = data.cursorBlueprint || data.base44Blueprint || data.loveableBlueprint || null;

            // Extract research data from the metadata if analysis data is generic
            const claudeStats = data.metadata?.claudeStats;
            let researchData = null;
            let validationData = null;

            if (claudeStats?.response) {
                try {
                    const parsedResponse = JSON.parse(claudeStats.response);
                    if (parsedResponse.cursorBlueprint) {
                        // Update blueprint with actual detailed data
                        const detailedBlueprint = parsedResponse.cursorBlueprint;

                        return {
                            originalIdea: data.originalIdea || 'Your app idea',
                            coreFeatures: parsedResponse.analysis?.coreFeatures || data.coreFeatures || [],

                            // Extract REAL tech stack from detailed blueprint
                            techStack: {
                                frontend: detailedBlueprint.frontendLayer?.framework || data.techStack?.frontend || 'Not specified',
                                backend: detailedBlueprint.backendLayer?.architecture || data.techStack?.backend || 'Not specified',
                                database: detailedBlueprint.backendLayer?.database || data.techStack?.database || 'Not specified',
                                authentication: detailedBlueprint.backendLayer?.authentication || data.techStack?.authentication || 'Not specified',
                                deployment: detailedBlueprint.deploymentLayer?.hosting || data.techStack?.deployment || 'Not specified'
                            },

                            targetAudience: parsedResponse.analysis?.targetAudience || data.targetAudience || 'General users',
                            challenges: parsedResponse.analysis?.challenges || data.challenges || [],

                            // Extract REAL development phases
                            developmentPhases: parsedResponse.analysis?.developmentPhases || data.developmentPhases || [{
                                title: 'Development Plan',
                                description: 'Implementation based on detailed analysis',
                                duration: parsedResponse.analysis?.estimatedTimeframe || data.estimatedTimeframe || '2-4 weeks',
                                tasks: parsedResponse.analysis?.coreFeatures || data.coreFeatures || []
                            }],

                            // Extract service recommendations from the actual response
                            serviceRecommendations: data.serviceRecommendations || {},

                            researchSummary: data.researchSummary || {},
                            researchEnhanced: data.researchEnhanced || false,
                            projectType: parsedResponse.analysis?.projectType || data.projectType || 'web-app',
                            category: parsedResponse.analysis?.category || data.category || 'productivity',
                            complexity: parsedResponse.analysis?.complexity || data.complexity || 'medium',
                            estimatedTimeframe: parsedResponse.analysis?.estimatedTimeframe || data.estimatedTimeframe || '1-2 months',
                            confidence: parsedResponse.analysis?.confidence || data.confidence || 0.8,
                            reasoning: parsedResponse.analysis?.reasoning || data.reasoning || '',
                            metadata: data.metadata || {},
                            detectedRequirements: data.detectedRequirements || [],
                            budgetAnalysis: data.budgetAnalysis || null,
                            freeTierAnalysis: data.research?.freeTierAnalysis || data.freeTierAnalysis || null
                        };
                    }
                } catch (parseError) {
                    // Fallback to original data
                }
            }

            // Fallback to original structure
            return {
                originalIdea: data.originalIdea || 'Your app idea',
                coreFeatures: data.coreFeatures || [],

                // Extract tech stack from the actual response structure
                techStack: data.techStack || blueprint?.techStack || {},

                targetAudience: data.targetAudience || 'General users',
                challenges: data.challenges || [],

                // Extract development phases from blueprint if available
                developmentPhases: data.developmentPhases || (blueprint ? [{
                    title: 'Development Plan',
                    description: blueprint.projectStructure?.configuration || 'Complete development setup',
                    duration: data.estimatedTimeframe || '2-4 weeks',
                    tasks: data.coreFeatures || []
                }] : []),

                // Extract service recommendations from the actual response
                serviceRecommendations: data.serviceRecommendations || {},

                researchSummary: data.researchSummary || {},
                researchEnhanced: data.researchEnhanced || false,
                projectType: data.projectType || 'web-app',
                category: data.category || 'productivity',
                complexity: data.complexity || 'medium',
                estimatedTimeframe: data.estimatedTimeframe || '1-2 months',
                confidence: data.confidence || 0.8,
                reasoning: data.reasoning || '',
                metadata: data.metadata || {},
                detectedRequirements: data.detectedRequirements || [],
                budgetAnalysis: data.budgetAnalysis || null,
                freeTierAnalysis: data.research?.freeTierAnalysis || data.freeTierAnalysis || null
            };
        }

        return {
            originalIdea: 'Your app idea',
            coreFeatures: [],
            techStack: {},
            targetAudience: 'General users',
            challenges: [],
            developmentPhases: [],
            serviceRecommendations: {},
            researchSummary: {},
            researchEnhanced: false,
            projectType: 'web-app',
            category: 'productivity',
            complexity: 'medium',
            estimatedTimeframe: '1-2 months',
            confidence: 0.8,
            reasoning: '',
            metadata: {},
            detectedRequirements: [],
            budgetAnalysis: null,
            freeTierAnalysis: null
        };
    };

    // Copy to clipboard utility
    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (error) {
            if (import.meta.env.DEV) {
            }
            return false;
        }
    };

    // Extract text content from elements
    const extractSectionText = (children) => {
        if (typeof children === 'string') return children;
        if (Array.isArray(children)) {
            return children.map(child =>
                typeof child === 'string' ? child : child.props?.children || ''
            ).join(' ');
        }
        return children?.props?.children || '';
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#7B61FF] border-t-transparent"></div>
                <Text variant="secondary" className="ml-3">Processing analysis...</Text>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="glass-panel p-8 text-center rounded-2xl">
                <Icon name="warning" size="2xl" className="mb-4 mx-auto text-[#FF6B6B]" />
                <Heading level={3} className="text-lg mb-2 text-white">Analysis Display Error</Heading>
                <Text variant="secondary" className="text-gray-300">{error}</Text>
            </div>
        );
    }

    if (!normalizedData) {
        return null;
    }

    return (
        <div className="space-y-6">
            {/* Original Idea */}
            <OriginalIdeaSection idea={normalizedData.originalIdea} />

            {/* NEW: Validation Status (Step 2 HLD Data) */}
            {normalizedData.researchSummary?.validationStatus && (
                <ValidationStatusSection
                    validationData={normalizedData.researchSummary}
                    metadata={normalizedData.metadata}
                />
            )}

            {/* Grid Layout for Main Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CoreFeaturesSection features={normalizedData.coreFeatures} />
                <TechStackSection techStack={normalizedData.techStack} />
                <TargetAudienceSection audience={normalizedData.targetAudience} />
                <ChallengesSection challenges={normalizedData.challenges} />
            </div>

            {/* Development Phases */}
            <DevelopmentPhasesSection phases={normalizedData.developmentPhases} />

            {/* Service Recommendations */}
            <ServiceRecommendationsSection
                serviceRecs={normalizedData.serviceRecommendations}
                requirements={normalizedData.detectedRequirements}
                budgetAnalysis={normalizedData.budgetAnalysis}
                freeTierAnalysis={normalizedData.freeTierAnalysis}
            />

            {/* Enhanced Service Recommendations - cleaner overview display */}
            {normalizedData.serviceRecommendations && (
                <DetailedServiceRecommendationsSection
                    serviceRecs={normalizedData.serviceRecommendations}
                    research={research}
                    finalStack={data?.finalTechStack || validation?.refinedArchitecture?.techStack || validation?.improvedArchitecture?.techStack}
                />
            )}

            {/* Research Summary */}
            {normalizedData.researchEnhanced && (
                <ResearchSummarySection
                    researchSummary={normalizedData.researchSummary}
                    researchEnhanced={normalizedData.researchEnhanced}
                />
            )}

            {/* Metadata */}
            <MetadataSection data={normalizedData} />
        </div>
    );
};

// Individual section components
const OriginalIdeaSection = ({ idea }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const MAX_LENGTH = 280;

    if (!idea) return null;

    const shouldTruncate = idea.length > MAX_LENGTH;
    const displayedText = isExpanded || !shouldTruncate ? idea : `${idea.substring(0, MAX_LENGTH)}...`;

    return (
        <div className="glass-panel p-6 rounded-2xl transition-all duration-300">
            <Heading level={3} className="text-lg mb-3 flex items-center">
                <Icon name="lightbulb" size="sm" className="mr-2 text-[#7B61FF]" />
                Your App Idea
                <CopyButton content={idea} />
            </Heading>
            <div className="relative">
                <Text variant="secondary" className="leading-relaxed text-gray-300 break-words whitespace-pre-wrap">
                    {displayedText}
                </Text>
                {shouldTruncate && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="mt-3 text-sm font-semibold text-[#7B61FF] hover:text-[#BA87FF] transition-colors flex items-center focus:outline-none group"
                    >
                        {isExpanded ? (
                            <>
                                Show Less <Icon name="chevronUp" size="xs" className="ml-1 group-hover:-translate-y-0.5 transition-transform" />
                            </>
                        ) : (
                            <>
                                Read More <Icon name="chevronDown" size="xs" className="ml-1 group-hover:translate-y-0.5 transition-transform" />
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

// NEW: Validation Status Section for Step 2 HLD Data
const ValidationStatusSection = ({ validationData, metadata }) => {
    const getValidationStatusColor = (status) => {
        return status === 'Valid' ? 'text-[#7B61FF]' : 'text-[#FFB800]';
    };

    const getScoreColor = (score) => {
        if (score === 'excellent') return 'text-[#7B61FF]';
        if (score === 'good') return 'text-[#CCCCCC]';
        return 'text-[#FFB800]';
    };

    const getScoreIcon = (score) => {
        if (score === 'excellent') return '🟢';
        if (score === 'good') return '🔵';
        return '🟡';
    };

    return (
        <div className="glass-panel p-6 rounded-2xl bg-gradient-to-r from-[#7B61FF]/10 to-[#9B59B6]/10 border border-[#7B61FF]/20 shadow-[0_0_30px_rgba(123,97,255,0.1)]">
            <Heading level={3} className="text-lg mb-4 flex items-center">
                <Icon name="check" size="sm" className="mr-2 text-[#7B61FF]" />
                Validation Results {metadata?.step && `(Step ${metadata.step})`}
                <Badge
                    variant={validationData.validationStatus === 'Valid' ? "success" : "warning"}
                    size="sm"
                    className="ml-3"
                >
                    {validationData.validationStatus}
                </Badge>
            </Heading>

            {/* Validation Scores Grid */}
            {validationData.validationResults && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
                    {Object.entries(validationData.validationResults).map(([key, score]) => (
                        <div key={key} className="bg-black/20 backdrop-blur-sm border border-white/5 rounded-xl p-3 text-center transition-all hover:bg-white/5">
                            <div className="text-lg mb-1">
                                <Icon name={score === 'excellent' ? 'check' : score === 'good' ? 'target' : 'warning'} size="sm" className={getScoreColor(score)} />
                            </div>
                            <Text variant="secondary" size="xs" weight="medium" className="mb-1 text-gray-400">
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </Text>
                            <Text size="xs" weight="semibold" className={getScoreColor(score)}>
                                {score}
                            </Text>
                        </div>
                    ))}
                </div>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 bg-black/20 border border-white/5 rounded-xl p-4">
                <div className="text-center">
                    <Text size="xl" weight="bold" className="text-green-400">{validationData.improvements || 0}</Text>
                    <Text variant="secondary" size="xs" className="text-gray-400">Improvements</Text>
                </div>
                <div className="text-center">
                    <Text size="xl" weight="bold" className="text-[#FFB800]">{validationData.concerns || 0}</Text>
                    <Text variant="secondary" size="xs" className="text-gray-400">Concerns</Text>
                </div>
                <div className="text-center">
                    <Text size="xl" weight="bold" className="text-[#7B61FF]">
                        {metadata?.validationScore || 'High'}
                    </Text>
                    <Text variant="secondary" size="xs" className="text-gray-400">Confidence</Text>
                </div>
            </div>

            {/* Risk Level */}
            {validationData.riskLevel && (
                <div className="mt-4 flex items-center justify-center">
                    <Text variant="secondary" size="sm" className="mr-2 text-gray-400">Overall Risk Level:</Text>
                    <Badge
                        variant={validationData.riskLevel === 'low' ? 'success' : validationData.riskLevel === 'medium' ? 'warning' : 'error'}
                        size="sm"
                    >
                        {validationData.riskLevel.toUpperCase()}
                    </Badge>
                </div>
            )}
        </div>
    );
};

const CoreFeaturesSection = ({ features }) => {
    if (!features || features.length === 0) return null;

    const numFeatures = features.length;
    const useGrid = numFeatures >= 8;

    return (
        <div className={`glass-panel p-6 rounded-2xl ${useGrid ? 'lg:col-span-2' : ''}`}>
            <Heading level={3} className="text-lg mb-4 flex items-center">
                <Icon name="star" size="sm" className="mr-2 text-[#7B61FF]" />
                Core Features
                <CopyButton content={features.map(f => f.title || f).join('\n')} />
            </Heading>
            <div className={useGrid ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'space-y-3'}>
                {features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                        <Icon name="check" size="xs" className="mr-3 text-green-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <Text variant="primary" weight="medium" size="sm" className="block break-words text-gray-200">
                                {feature.title || feature}
                            </Text>
                            {feature.description && (
                                <Text variant="secondary" size="sm" className="mt-1 break-words leading-relaxed text-gray-400">
                                    {feature.description}
                                </Text>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TechStackSection = ({ techStack }) => {
    if (!techStack || Object.keys(techStack).length === 0) return null;

    const stackItems = typeof techStack === 'object'
        ? Object.entries(techStack)
        : techStack.map(item => [item.category || 'Technology', item.name || item]);

    const numItems = stackItems.length;
    const useGrid = numItems >= 8;

    return (
        <div className={`glass-panel p-6 rounded-2xl ${useGrid ? 'lg:col-span-2' : ''}`}>
            <Heading level={3} className="text-lg mb-4 flex items-center">
                <Icon name="settings" size="sm" className="mr-2 text-[#7B61FF]" />
                Technology Stack
                <CopyButton content={stackItems.map(([cat, tech]) => `${cat}: ${tech}`).join('\n')} />
            </Heading>
            <div className={useGrid ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3' : 'space-y-3'}>
                {stackItems.map(([category, tech], index) => (
                    <TechStackItem
                        key={index}
                        category={category}
                        tech={tech}
                        index={index}
                        useGrid={useGrid}
                    />
                ))}
            </div>
        </div>
    );
};

const TechStackItem = ({ category, tech, index, useGrid }) => {
    return (
        <div className="bg-white/5 border border-white/5 p-3 rounded-xl transition-all hover:bg-white/10 hover:border-white/10 hover:translate-y-[-1px]">
            <div className="flex flex-col">
                <Text variant="primary" weight="semibold" size="sm" className="break-words text-gray-200">
                    {category}
                </Text>
                <Text variant="secondary" size="sm" className="mt-1 break-words text-[#7B61FF]">
                    {Array.isArray(tech) ? tech.join(', ') : (typeof tech === 'object' && tech !== null ? Object.entries(tech).map(([k, v]) => `${k}: ${v}`).join(', ') : tech)}
                </Text>
            </div>
        </div>
    );
};

const TargetAudienceSection = ({ audience }) => (
    <div className="glass-panel p-6 rounded-2xl">
        <Heading level={3} className="text-lg mb-4 flex items-center">
            <Icon name="target" size="sm" className="mr-2 text-[#7B61FF]" />
            Target Audience
            <CopyButton content={typeof audience === 'string' ? audience : JSON.stringify(audience, null, 2)} />
        </Heading>
        {typeof audience === 'string' ? (
            <Text variant="secondary" className="leading-relaxed text-gray-300">{audience}</Text>
        ) : (
            <div className="space-y-3">
                {audience.primary && (
                    <div>
                        <Text variant="primary" weight="semibold" className="inline text-gray-300">Primary: </Text>
                        <Text variant="secondary" className="inline text-gray-400">{audience.primary}</Text>
                    </div>
                )}
                {audience.secondary && (
                    <div>
                        <Text variant="primary" weight="semibold" className="inline text-gray-300">Secondary: </Text>
                        <Text variant="secondary" className="inline text-gray-400">{audience.secondary}</Text>
                    </div>
                )}
                {audience.demographics && (
                    <div>
                        <Text variant="primary" weight="semibold" className="inline text-gray-300">Demographics: </Text>
                        <Text variant="secondary" className="inline text-gray-400">{audience.demographics}</Text>
                    </div>
                )}
            </div>
        )}
    </div>
);

const ChallengesSection = ({ challenges }) => {
    if (!challenges || challenges.length === 0) return null;

    const numChallenges = challenges.length;
    const useGrid = numChallenges >= 6;

    return (
        <div className={`glass-panel p-6 rounded-2xl ${useGrid ? 'lg:col-span-2' : ''}`}>
            <Heading level={3} className="text-lg mb-4 flex items-center">
                <Icon name="warning" size="sm" className="mr-2 text-[#FFB800]" />
                Challenges & Considerations
                <CopyButton content={challenges.map(c => c.title || c).join('\n')} />
            </Heading>
            <div className={useGrid ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : 'space-y-3'}>
                {challenges.map((challenge, index) => (
                    <div key={index} className="flex items-start">
                        <Icon name="warning" size="xs" className="mr-3 text-[#FFB800] flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <Text variant="primary" weight="medium" size="sm" className="block break-words text-gray-200">
                                {challenge.title || challenge}
                            </Text>
                            {challenge.description && (
                                <Text variant="secondary" size="sm" className="mt-1 break-words leading-relaxed text-gray-400">
                                    {challenge.description}
                                </Text>
                            )}
                            {challenge.mitigation && (
                                <Text variant="accent" size="sm" className="mt-1 break-words leading-relaxed text-[#7B61FF]">
                                    <strong className="text-white">Mitigation:</strong> {challenge.mitigation}
                                </Text>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const DevelopmentPhasesSection = ({ phases }) => {
    if (!phases || phases.length === 0) return null;

    return (
        <div className="glass-panel p-6 rounded-2xl">
            <Heading level={3} className="text-lg mb-4 flex items-center">
                <Icon name="clipboard" size="sm" className="mr-2 text-[#7B61FF]" />
                Development Phases
                <CopyButton content={phases.map(p => `${p.title || `Phase ${phases.indexOf(p) + 1}`}: ${p.description || p}`).join('\n')} />
            </Heading>
            <div className="space-y-4">
                {phases.map((phase, index) => (
                    <div key={index} className="bg-white/5 border border-white/5 p-4 rounded-xl hover:bg-white/10 transition-colors">
                        <div className="flex items-center mb-2">
                            <div className="bg-[#7B61FF] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 shadow-[0_0_10px_rgba(123,97,255,0.4)]">
                                {index + 1}
                            </div>
                            <Heading level={4} className="font-semibold break-words text-gray-200">
                                {phase.title || `Phase ${index + 1}`}
                            </Heading>
                        </div>
                        <Text variant="secondary" size="sm" className="mb-2 break-words leading-relaxed text-gray-400 pl-9">
                            {phase.description || phase}
                        </Text>
                        {phase.duration && (
                            <div className="text-[#7B61FF] text-sm font-medium break-words flex items-center pl-9">
                                <Icon name="clock" size="xs" className="mr-1" />
                                {phase.duration}
                            </div>
                        )}
                        {phase.tasks && phase.tasks.length > 0 && (
                            <div className="mt-3">
                                <Text variant="secondary" weight="medium" size="sm" className="mb-2">Key Tasks:</Text>
                                <ul className="space-y-1">
                                    {phase.tasks.map((task, taskIndex) => (
                                        <li key={taskIndex} className="text-sm text-[#CCCCCC] flex items-start">
                                            <Icon name="check" size="xs" className="mr-2 text-green-400 flex-shrink-0 mt-0.5" />
                                            <Text variant="secondary" size="sm" className="break-words leading-relaxed">{task}</Text>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {phase.deliverables && phase.deliverables.length > 0 && (
                            <div className="mt-2">
                                <Text variant="secondary" weight="medium" size="sm" className="mb-1">Deliverables:</Text>
                                <Text variant="secondary" size="sm" className="break-words leading-relaxed text-gray-400">
                                    {phase.deliverables.join(', ')}
                                </Text>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const ServiceRecommendationsSection = ({ serviceRecs, requirements, budgetAnalysis, freeTierAnalysis }) => {
    if (!serviceRecs || Object.keys(serviceRecs).length === 0) return null;

    const categories = [
        { key: 'hosting', title: 'Hosting & Infrastructure', icon: '🌐' },
        {
            key: 'backend', title: 'Backend Services', icon:
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
        },
        { key: 'authentication', title: 'Authentication', icon: '🔐' },
        { key: 'database', title: 'Database', icon: '🗄️' }
    ];

    return (
        <div className="glass-panel p-6 rounded-2xl">
            <div className="mb-6">
                <div className="flex items-center mb-4">
                    <Icon name="settings" size="lg" className="mr-3 text-[#7B61FF] flex-shrink-0" />
                    <div className="min-w-0">
                        <Heading level={3} className="text-lg break-words">
                            Research-Backed Service Stack
                        </Heading>
                        <Text variant="secondary" size="sm" className="break-words text-gray-400">
                            Optimized recommendations based on your requirements
                        </Text>
                    </div>
                </div>

                {/* Free-tier analysis */}
                {freeTierAnalysis && (
                    <FreeTierAnalysis analysis={freeTierAnalysis} />
                )}

                {/* Budget and requirements summary */}
                {(budgetAnalysis || requirements) && (
                    <div className="mb-4 flex flex-wrap gap-2">
                        {budgetAnalysis?.preference && (
                            <Badge variant="info" size="sm">
                                {budgetAnalysis.preference} budget
                            </Badge>
                        )}
                        {requirements && requirements.length > 0 && (
                            <Badge variant="success" size="sm">
                                {requirements.length} requirements detected
                            </Badge>
                        )}
                    </div>
                )}

                {/* Service categories */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {categories.map(category => {
                        const services = serviceRecs[category.key];
                        if (!services || services.length === 0) return null;

                        const topService = services[0];
                        return (
                            <ServiceCategoryCard
                                key={category.key}
                                category={category}
                                topService={topService}
                                totalServices={services.length}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const FreeTierAnalysis = ({ analysis }) => {
    const getPriorityVariant = (priority) => {
        switch (priority) {
            case 'high': return 'info';
            case 'medium': return 'success';
            default: return 'secondary';
        }
    };

    return (
        <div className="mb-4 bg-white/5 border border-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors">
            <div className="flex items-center mb-2">
                <Icon name="gift" size="sm" className="mr-2 text-green-400" />
                <Heading level={4} className="font-medium text-gray-200">Free-Tier Analysis</Heading>
                <Badge variant={getPriorityVariant(analysis.priority)} size="sm" className="ml-2">
                    {analysis.priority} priority
                </Badge>
            </div>
            <Text variant="secondary" size="sm" className="mb-2 text-gray-400">{analysis.reasoning}</Text>
            <Text variant="accent" size="xs" className="text-[#7B61FF]">
                Score: {analysis.score}/10
                {analysis.confidence && ` • Confidence: ${analysis.confidence}%`}
            </Text>
        </div>
    );
};

const ServiceCategoryCard = ({ category, topService, totalServices }) => (
    <div className="bg-white/5 border border-white/5 rounded-xl p-4 hover:bg-white/10 transition-all hover:translate-y-[-2px]">
        <Heading level={4} className="font-semibold mb-2 flex items-center text-gray-200">
            <Icon name="settings" size="xs" className="mr-2 text-[#7B61FF] flex-shrink-0" />
            <span className="break-words">{category.title}</span>
        </Heading>
        <div className="bg-black/20 border border-white/5 rounded-lg p-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-1 sm:space-y-0">
                <Text variant="primary" weight="medium" size="sm" className="break-words text-white">{topService.name}</Text>
                <Badge variant="success" size="xs" className="self-start sm:self-auto">
                    {Math.round(topService.score * 100)}% match
                </Badge>
            </div>
            {topService.pricing && (
                <Text variant="secondary" size="sm" className="mb-2 break-words text-gray-400">{topService.pricing}</Text>
            )}
            <div className="flex flex-wrap gap-1">
                {(topService.strengths || ['Fast setup', 'Great documentation', 'Community support'])
                    .slice(0, 3)
                    .map((strength, index) => (
                        <Badge key={index} variant="secondary" size="xs" className="break-words bg-white/10 text-gray-300">
                            {strength}
                        </Badge>
                    ))}
            </div>
        </div>
        {totalServices > 1 && (
            <Text variant="muted" size="xs" className="mt-2 text-gray-500">
                +{totalServices - 1} alternative{totalServices > 2 ? 's' : ''} analyzed
            </Text>
        )}
    </div>
);

const ResearchSummarySection = ({ researchSummary, researchEnhanced }) => (
    <div className="glass-panel p-6 rounded-2xl">
        <div className="mb-2">
            <div className="flex items-center mb-2">
                <Icon name="search" size="sm" className="mr-2 text-[#7B61FF]" />
                <Heading level={4} className="font-semibold text-gray-200">Deep Research Analysis</Heading>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                    <Text size="lg" weight="bold" className="text-[#7B61FF]">
                        {researchSummary?.totalRequirements || 0}
                    </Text>
                    <Text variant="secondary" size="sm" className="text-gray-400">Requirements Detected</Text>
                </div>
                <div className="text-center">
                    <Text size="lg" weight="bold" className="text-green-400">
                        {researchSummary?.budgetPreference || 'N/A'}
                    </Text>
                    <Text variant="secondary" size="sm" className="text-gray-400">Budget Profile</Text>
                </div>
                <div className="text-center">
                    <Text size="lg" weight="bold" className="text-[#7B61FF]">
                        {researchSummary?.topRecommendation || 'N/A'}
                    </Text>
                    <Text variant="secondary" size="sm" className="text-gray-400">Top Platform</Text>
                </div>
            </div>
        </div>
    </div>
);

const MetadataSection = ({ data }) => {
    if (!data.analysisDate && !data.modelUsed && !data.confidence && !data.processingTime) {
        return null;
    }

    return (
        <div className="glass-panel p-4 rounded-xl border border-white/5">
            <Heading level={4} className="text-sm font-medium mb-2 text-gray-300">Analysis Details</Heading>
            <div className="grid grid-cols-2 gap-4 text-xs">
                {data.analysisDate && (
                    <div>
                        <Text variant="secondary" weight="semibold" size="xs" className="inline text-gray-400">Generated: </Text>
                        <Text variant="muted" size="xs" className="inline text-gray-500">{new Date(data.analysisDate).toLocaleString()}</Text>
                    </div>
                )}
                {data.modelUsed && (
                    <div>
                        <Text variant="secondary" weight="semibold" size="xs" className="inline text-gray-400">AI Model: </Text>
                        <Text variant="muted" size="xs" className="inline text-gray-500">{data.modelUsed}</Text>
                    </div>
                )}
                {data.confidence && (
                    <div>
                        <Text variant="secondary" weight="semibold" size="xs" className="inline text-gray-400">Confidence: </Text>
                        <Text variant="muted" size="xs" className="inline text-gray-500">{Math.round(data.confidence * 100)}%</Text>
                    </div>
                )}
                {data.processingTime && (
                    <div>
                        <Text variant="secondary" weight="semibold" size="xs" className="inline text-gray-400">Processing Time: </Text>
                        <Text variant="muted" size="xs" className="inline text-gray-500">{data.processingTime}ms</Text>
                    </div>
                )}
                {data.researchEnhanced && (
                    <div>
                        <Text variant="secondary" weight="semibold" size="xs" className="inline text-gray-400">Research Enhanced: </Text>
                        <Icon name="check" size="xs" className="inline text-green-400" />
                        <Text variant="success" size="xs" className="inline ml-1 text-green-400">Yes</Text>
                    </div>
                )}
            </div>
        </div>
    );
};

// Copy button component
const CopyButton = ({ content }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
        }
    };

    return (
        <Button
            onClick={handleCopy}
            variant="ghost"
            size="sm"
            className="ml-2 p-1"
            title="Copy section content"
        >
            <Icon name={copied ? "check" : "clipboard"} size="xs" className={copied ? "text-green-400" : "text-[#8B8E98] hover:text-[#7B61FF]"} />
        </Button>
    );
};

// NEW: Comprehensive Research Section - showing all the research data users pay for
const ComprehensiveResearchSection = ({ research }) => {
    if (!research) return null;

    return (
        <div className="glass-panel p-4 sm:p-6 rounded-2xl">
            <Heading level={3} className="text-base sm:text-lg mb-3 sm:mb-4 flex items-center text-white">
                <Icon name="search" size="sm" className="mr-2 text-white" />
                Comprehensive Research Analysis
                <CopyButton content={JSON.stringify(research, null, 2)} />
            </Heading>

            {/* Research Sources - Enhanced to support both new array and legacy object formats */}
            {research.researchSources && (
                <div className="mb-6 bg-[#2A2D36] border border-[#3A3D46] rounded-lg p-4">
                    <Heading level={4} className="mb-3 flex items-center text-white">
                        <Icon name="document" size="xs" className="mr-2" />
                        Research Sources & Documentation
                    </Heading>

                    {/* 🔧 FIXED: Support both new array format and legacy object format */}
                    {Array.isArray(research.researchSources) ? (
                        // New format: Array of {title, url, type, relevance} objects
                        <div className="space-y-3">
                            <Text variant="secondary" size="sm" className="mb-3">
                                Found {research.researchSources.length} research sources from web search:
                            </Text>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                {research.researchSources.map((source, idx) => (
                                    <div key={idx} className="bg-[#1A1D23] border border-[#3A3D46] rounded-lg p-3">
                                        <div className="flex items-start">
                                            <Icon name="check" size="xs" className="mr-2 text-green-400 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <Text variant="primary" weight="medium" size="sm" className="mb-1">
                                                    {source.url ? (
                                                        <a href={source.url} target="_blank" rel="noopener noreferrer"
                                                            className="text-white hover:text-gray-300 underline">
                                                            {source.title}
                                                        </a>
                                                    ) : (
                                                        source.title
                                                    )}
                                                </Text>
                                                <Text variant="secondary" size="xs" className="block mb-1">
                                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-[#7B61FF] bg-opacity-20 text-[#7B61FF]">
                                                        {source.type || 'documentation'}
                                                    </span>
                                                </Text>
                                                {source.relevance && (
                                                    <Text variant="secondary" size="xs" className="italic">
                                                        {source.relevance}
                                                    </Text>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        // Legacy format: Object with nested arrays
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                                <Text variant="primary" weight="medium" size="sm" className="mb-2">Awesome Lists Consulted:</Text>
                                <ul className="space-y-1">
                                    {research.researchSources.awesomeListsConsulted?.map((list, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <Icon name="check" size="xs" className="mr-2 text-white flex-shrink-0 mt-0.5" />
                                            <Text variant="secondary" size="sm">{list}</Text>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <Text variant="primary" weight="medium" size="sm" className="mb-2">Service Documentation:</Text>
                                <ul className="space-y-1">
                                    {research.researchSources.serviceDocumentationReviewed?.map((doc, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <Icon name="check" size="xs" className="mr-2 text-white flex-shrink-0 mt-0.5" />
                                            <Text variant="secondary" size="sm">{doc}</Text>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <Text variant="primary" weight="medium" size="sm" className="mb-2">Community Resources:</Text>
                                <ul className="space-y-1">
                                    {research.researchSources.communityResourcesFound?.map((resource, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <Icon name="check" size="xs" className="mr-2 text-white flex-shrink-0 mt-0.5" />
                                            <Text variant="secondary" size="sm">{resource}</Text>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Research Findings */}
            {research.researchedServices && (
                <div className="mb-6 bg-[#2A2D36] border border-[#3A3D46] rounded-lg p-4">
                    <Heading level={4} className="mb-3 flex items-center text-[#7B61FF]">
                        <Icon name="search" size="xs" className="mr-2" />
                        Service Discovery Results
                    </Heading>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-[#1A1A1A] rounded-lg p-3 border border-[#3A3D46]">
                            <Text variant="primary" size="xl" weight="bold" className="mb-1">
                                {research.researchedServices.totalFreeServices}
                            </Text>
                            <Text variant="secondary" size="sm">Total Free Services Found</Text>
                        </div>
                        <div className="bg-[#1A1A1A] rounded-lg p-3 border border-[#3A3D46]">
                            <Text variant="primary" weight="medium" size="sm" className="mb-2">Discovered via Awesome Lists:</Text>
                            <div className="flex flex-wrap gap-1">
                                {research.researchedServices.discoveredViaAwesomeLists?.map((service, idx) => (
                                    <Badge key={idx} variant="info" size="xs">
                                        {service}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Current Free Limits */}
            {research.researchedServices?.currentFreeLimits && (
                <div className="bg-[#2A2D36] border border-[#3A3D46] rounded-lg p-4">
                    <Heading level={4} className="mb-3 flex items-center text-white">
                        <Icon name="chart" size="xs" className="mr-2" />
                        Current Free Tier Limits (Verified)
                    </Heading>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {Object.entries(research.researchedServices.currentFreeLimits).map(([service, limit]) => (
                            <div key={service} className="bg-[#2A2D36] rounded-lg p-3 border border-[#3A3D46]">
                                <Text variant="primary" weight="medium" size="sm" className="capitalize mb-1">{service}</Text>
                                <Text variant="secondary" size="sm">{limit}</Text>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// NEW: Validation Results Section - showing architectural validation data
const ValidationResultsSection = ({ validation }) => {
    if (!validation) return null;

    return (

        <div className="glass-panel p-6 rounded-2xl">
            <Heading level={3} className="text-lg mb-4 flex items-center text-white">
                <Icon name="check" size="sm" className="mr-2 text-[#7B61FF]" />
                Architecture Validation Results
                <Badge
                    variant={validation.validation?.isValid ? "success" : "warning"}
                    size="sm"
                    className="ml-3"
                >
                    {validation.validation?.isValid ? 'Valid Architecture' : 'Needs Improvements'}
                </Badge>
                <CopyButton content={JSON.stringify(validation, null, 2)} />
            </Heading>

            {/* Validation Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Improvements */}
                {validation.validation?.improvements && validation.validation.improvements.length > 0 && (
                    <div className="bg-[#7B61FF]/10 border border-[#7B61FF]/20 rounded-xl p-4">
                        <Heading level={4} className="mb-3 flex items-center text-[#7B61FF]">
                            <Icon name="lightbulb" size="xs" className="mr-2" />
                            Recommended Improvements
                        </Heading>
                        <ul className="space-y-2">
                            {validation.validation.improvements.map((improvement, idx) => (
                                <li key={idx} className="flex items-start">
                                    <Icon name="check" size="xs" className="mr-2 text-[#7B61FF] flex-shrink-0 mt-0.5" />
                                    <Text variant="secondary" size="sm" className="text-gray-300">{improvement}</Text>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Concerns */}
                {validation.validation?.concerns && validation.validation.concerns.length > 0 && (
                    <div className="bg-white/5 border border-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors">
                        <Heading level={4} className="mb-3 flex items-center text-[#7B61FF]">
                            <Icon name="warning" size="xs" className="mr-2" />
                            Architecture Concerns
                        </Heading>
                        <ul className="space-y-2">
                            {validation.validation.concerns.map((concern, idx) => (
                                <li key={idx} className="flex items-start">
                                    <Icon name="warning" size="xs" className="mr-2 text-[#7B61FF] flex-shrink-0 mt-0.5" />
                                    <Text variant="secondary" size="sm" className="text-gray-300">{concern}</Text>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Better Alternatives (if available) */}
            {validation.betterFreeAlternatives && (
                <div className="bg-white/5 border border-white/5 rounded-xl p-4 mb-6 hover:bg-white/10 transition-colors">
                    <Heading level={4} className="mb-3 flex items-center text-[#7B61FF]">
                        <Icon name="refresh" size="xs" className="mr-2" />
                        Better Free Alternatives Discovered
                    </Heading>
                    <div className="space-y-3">
                        {validation.betterFreeAlternatives.map((alternative, idx) => (
                            <div key={idx} className="bg-black/20 rounded-lg p-3 border border-white/5">
                                <div className="mb-1">
                                    <Text variant="secondary" size="sm" className="inline text-gray-400">Instead of: </Text>
                                    <Text variant="muted" size="sm" className="inline text-gray-400">{alternative['instead-of']}</Text>
                                </div>
                                <div className="mb-1">
                                    <Text variant="secondary" size="sm" className="inline text-gray-400">Use: </Text>
                                    <Text variant="accent" size="sm" className="inline text-[#7B61FF]">{alternative.use}</Text>
                                </div>
                                <Text variant="secondary" size="sm" className="text-gray-300">
                                    Reason: {alternative.reason}
                                </Text>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Final Recommendations */}
            {validation.validation?.recommendations && (
                <div className="bg-white/5 border border-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors">
                    <Heading level={4} className="mb-2 flex items-center text-white">
                        <Icon name="target" size="xs" className="mr-2 text-[#7B61FF]" />
                        Final Recommendations
                    </Heading>
                    <Text variant="secondary" size="sm" className="leading-relaxed text-gray-300">
                        {validation.validation.recommendations}
                    </Text>
                </div>
            )}
        </div>
    );
};

// NEW: Confidence Dashboard - showing AI processing metrics
const ConfidenceDashboardSection = ({ metadata }) => {
    if (!metadata) return null;

    return (
        <div className="glass-panel p-6 rounded-2xl">
            <Heading level={3} className="text-lg mb-4 flex items-center">
                <Icon name="target" size="sm" className="mr-2 text-white" />
                AI Analysis Confidence Dashboard
                <CopyButton content={JSON.stringify(metadata, null, 2)} />
            </Heading>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Claude Confidence */}
                {metadata.claudeConfidence && (
                    <div className="bg-white/5 border border-white/5 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
                        <Text variant="primary" size="xl" weight="bold" className="mb-1 text-white">
                            {Math.round(metadata.claudeConfidence * 100)}%
                        </Text>
                        <Text variant="secondary" size="sm" className="text-gray-400">Claude Confidence</Text>
                    </div>
                )}

                {/* Consensus Score */}
                {metadata.consensusScore && (
                    <div className="bg-white/5 border border-white/5 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
                        <Text variant="primary" size="xl" weight="bold" className="mb-1 text-white">
                            {Math.round(metadata.consensusScore * 100)}%
                        </Text>
                        <Text variant="secondary" size="sm" className="text-gray-400">Consensus Score</Text>
                    </div>
                )}

                {/* Platform */}
                {metadata.platform && (
                    <div className="bg-white/5 border border-white/5 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
                        <Text variant="primary" size="lg" weight="bold" className="mb-1 capitalize text-white">
                            {metadata.platform}
                        </Text>
                        <Text variant="secondary" size="sm" className="text-gray-400">Target Platform</Text>
                    </div>
                )}

                {/* Timestamp */}
                {metadata.timestamp && (
                    <div className="bg-white/5 border border-white/5 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
                        <Text variant="secondary" size="sm" weight="bold" className="mb-1 text-gray-300">
                            {new Date(metadata.timestamp).toLocaleTimeString()}
                        </Text>
                        <Text variant="muted" size="sm" className="text-gray-500">Generated At</Text>
                    </div>
                )}
            </div>

            {/* Processing Stats */}
            {(metadata.claudeStats || data?.metadata?.processingStats || data?.processingStats) && (
                <div className="bg-white/5 border border-white/5 rounded-xl p-4">
                    <Heading level={4} className="mb-3 flex items-center text-gray-200">
                        <Icon name="chart" size="xs" className="mr-2 text-[#7B61FF]" />
                        Processing Statistics
                    </Heading>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        {((metadata.claudeStats || data?.metadata?.processingStats || {}).usage) && (
                            <div>
                                <Text variant="secondary" weight="medium" size="sm" className="mb-2 text-gray-400">Token Usage:</Text>
                                <div className="space-y-1">
                                    <Text variant="muted" size="sm" className="text-gray-500">Input: {(metadata.claudeStats?.usage?.inputTokens || data?.metadata?.processingStats?.usage?.inputTokens || 0).toLocaleString()}</Text>
                                    <Text variant="muted" size="sm" className="text-gray-500">Output: {(metadata.claudeStats?.usage?.outputTokens || data?.metadata?.processingStats?.usage?.outputTokens || 0).toLocaleString()}</Text>
                                    <Text variant="muted" size="sm" className="text-gray-500">Total: {(metadata.claudeStats?.usage?.totalTokens || data?.metadata?.processingStats?.usage?.totalTokens || 0).toLocaleString()}</Text>
                                </div>
                            </div>
                        )}

                        {((metadata.claudeStats || data?.metadata?.processingStats || {}).cost) && (
                            <div>
                                <Text variant="secondary" weight="medium" size="sm" className="mb-2 text-gray-400">Cost Analysis:</Text>
                                <div className="space-y-1">
                                    <Text variant="muted" size="sm" className="text-gray-500">Total: ${((metadata.claudeStats?.cost?.totalCost ?? data?.metadata?.processingStats?.cost?.totalCost) ?? 0).toFixed(4)}</Text>
                                    <Text variant="muted" size="sm" className="text-gray-500">Model: {metadata.claudeStats?.model || data?.metadata?.processingStats?.model || 'N/A'}</Text>
                                    <Text variant="muted" size="sm" className="text-gray-500">Credits: {metadata.claudeStats?.credits?.transaction?.amountSpent || data?.metadata?.processingStats?.credits?.transaction?.amountSpent || 0}</Text>
                                </div>
                            </div>
                        )}

                        {((metadata.claudeStats || data?.metadata?.processingStats || {}).timing) && (
                            <div>
                                <Text variant="secondary" weight="medium" size="sm" className="mb-2 text-gray-400">Performance:</Text>
                                <div className="space-y-1">
                                    <Text variant="muted" size="sm" className="text-gray-500">Processing: {(metadata.claudeStats?.timing?.totalTime || data?.metadata?.processingStats?.timing?.totalTime || 0)}ms</Text>
                                    <Text variant="muted" size="sm" className="text-gray-500">Fallback: {metadata.claudeStats?.fallbackLevel || data?.metadata?.processingStats?.fallbackLevel || 'None'}</Text>
                                    <div className="flex items-center">
                                        <Text variant="muted" size="sm" className="mr-1 text-gray-500">Success: </Text>
                                        <Icon
                                            name={(metadata.claudeStats?.success || data?.metadata?.processingStats?.success) ? "check" : "x"}
                                            size="xs"
                                            className={(metadata.claudeStats?.success || data?.metadata?.processingStats?.success) ? "text-green-400" : "text-red-400"}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// NEW: Detailed Service Recommendations - prefer validated/final tech stack; show research as reference
const DetailedServiceRecommendationsSection = ({ serviceRecs, research, finalStack }) => {
    const hasAny = !!serviceRecs || !!finalStack || !!research?.techStack;
    if (!hasAny) return null;

    return (
        <div className="glass-panel p-6 rounded-2xl">
            <Heading level={3} className="text-lg mb-4 flex items-center">
                <Icon name="settings" size="sm" className="mr-2 text-[#7B61FF]" />
                Detailed Service Recommendations
                <CopyButton content={JSON.stringify({ serviceRecs, techStack: finalStack || research?.techStack }, null, 2)} />
            </Heading>

            {/* Validated/Final Tech Stack (preferred) */}
            {finalStack && Object.keys(finalStack).length > 0 && (
                <div className="mb-6 bg-white/5 border border-white/5 rounded-xl p-4">
                    <Heading level={4} className="font-semibold mb-3 flex items-center text-[#7B61FF]">
                        <Icon name="shield" size="xs" className="mr-2" />
                        Validated Technology Stack
                    </Heading>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(finalStack).map(([category, tech]) => (
                            <div key={category} className="bg-white/5 rounded-lg p-3 border border-white/5 hover:bg-white/10 transition-colors">
                                <Text variant="accent" weight="medium" size="sm" className="capitalize mb-1 text-[#7B61FF]">{category}</Text>
                                <Text variant="secondary" size="sm" className="text-gray-300">{Array.isArray(tech) ? tech.join(', ') : (typeof tech === 'object' && tech !== null ? Object.entries(tech).map(([k, v]) => `${k}: ${v}`).join(', ') : tech)}</Text>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Tech Stack from Research (reference) */}
            {(!finalStack || Object.keys(finalStack).length === 0) && research?.techStack && (
                <div className="mb-6 bg-[#7B61FF]/10 border border-[#7B61FF]/20 rounded-xl p-4">
                    <Heading level={4} className="font-semibold mb-3 flex items-center text-[#7B61FF]">
                        <Icon name="settings" size="xs" className="mr-2" />
                        Research-Backed Technology Stack
                    </Heading>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(research.techStack).map(([category, tech]) => (
                            <div key={category} className="bg-white/5 rounded-lg p-3 border border-white/5 hover:bg-white/10 transition-colors">
                                <Text variant="accent" weight="medium" size="sm" className="capitalize mb-1 text-[#7B61FF]">{category}</Text>
                                <Text variant="secondary" size="sm" className="text-gray-300">{Array.isArray(tech) ? tech.join(', ') : (typeof tech === 'object' && tech !== null ? Object.entries(tech).map(([k, v]) => `${k}: ${v}`).join(', ') : tech)}</Text>
                                {research.researchedServices?.currentFreeLimits?.[category] && (
                                    <Text variant="success" size="xs" className="mt-1 text-green-400">
                                        Free tier: {research.researchedServices.currentFreeLimits[category]}
                                    </Text>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Additional Services */}
            {research?.techStack?.additionalServices && (
                <div className="bg-white/5 border border-white/5 rounded-xl p-4">
                    <Heading level={4} className="font-semibold mb-3 flex items-center text-[#7B61FF]">
                        <Icon name="plus" size="xs" className="mr-2" />
                        Additional Researched Services
                    </Heading>
                    <div className="flex flex-wrap gap-2">
                        {research.techStack.additionalServices.map((service, idx) => (
                            <Badge key={idx} variant="info" size="sm">
                                {service}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// NEW: Cost Analysis Section - showing pricing and upgrade paths
const CostAnalysisSection = ({ costData, research }) => {
    if (!costData) return null;

    return (
        <div className="glass-panel p-6 rounded-2xl">
            <Heading level={3} className="text-lg mb-4 flex items-center">
                <Icon name="money" size="sm" className="mr-2 text-white" />
                Cost Analysis & Upgrade Planning
                <CopyButton content={JSON.stringify(costData, null, 2)} />
            </Heading>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Current Cost */}
                <div className="bg-white/5 border border-white/5 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
                    <Text variant="primary" size="xl" weight="bold" className="mb-2 text-white">
                        {costData.monthly || '$0'}
                    </Text>
                    <Text variant="secondary" size="sm" className="text-gray-400">Monthly Cost</Text>
                    <Text variant="muted" size="xs" className="mt-1 text-gray-500">
                        Using {costData.freeServicesUsed || 0} free services
                    </Text>
                </div>

                {/* Free Services Count */}
                <div className="bg-white/5 border border-white/5 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
                    <Text variant="primary" size="xl" weight="bold" className="mb-2 text-white">
                        {research?.researchedServices?.totalFreeServices || costData.freeServicesUsed || 0}
                    </Text>
                    <Text variant="secondary" size="sm" className="text-gray-400">Free Services</Text>
                    <Text variant="secondary" size="xs" className="mt-1 text-gray-500">
                        Researched and verified
                    </Text>
                </div>

                {/* Upgrade Threshold */}
                <div className="bg-[#FFB800]/10 border border-[#FFB800]/20 rounded-xl p-4 text-center hover:bg-[#FFB800]/20 transition-colors">
                    <Text size="sm" weight="bold" className="mb-2 text-[#FFB800]">
                        First Paid Upgrade
                    </Text>
                    <Text size="xs" className="leading-relaxed text-[#FFB800]">
                        {costData.firstPaidUpgrade || 'Not specified'}
                    </Text>
                </div>
            </div>

            {/* Reasoning */}
            {research?.reasoning && (
                <div className="mt-6 bg-white/5 border border-white/5 rounded-xl p-4">
                    <Heading level={4} className="mb-2 flex items-center text-gray-200">
                        <Icon name="clipboard" size="xs" className="mr-2 text-[#7B61FF]" />
                        Cost Strategy Reasoning
                    </Heading>
                    <Text variant="secondary" size="sm" className="leading-relaxed text-gray-400">
                        {research.reasoning}
                    </Text>
                </div>
            )}
        </div>
    );
};

// NEW: Executive Summary Overview Component - Clean dashboard for decision makers
const ExecutiveSummaryOverview = ({
    data,
    research,
    validation,
    platform,
    originalIdea,
    answers,
    onNavigateToTab
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    // Extract final validated data (prefer refined → improved)
    const refinedTS = validation?.refinedArchitecture?.techStack || {};
    const improvedTS = validation?.improvedArchitecture?.techStack || {};
    const baseValidated = Object.keys(refinedTS).length > 0 ? refinedTS : (Object.keys(improvedTS).length > 0 ? improvedTS : (research?.techStack || {}));

    // Apply alternatives and fill missing supplemental keys
    // FIX: betterFreeAlternatives is at validation.betterFreeAlternatives (not validation.validation.betterFreeAlternatives)
    const applyAlternatives = (stackInput) => {
        const alt = validation?.betterFreeAlternatives;
        if (!alt || !Array.isArray(alt) || alt.length === 0) return stackInput;
        const current = { ...stackInput };
        alt.forEach(change => {
            if (!change || !change["instead-of"] || !change.use) return;
            const instead = String(change["instead-of"]).toLowerCase();
            Object.keys(current).forEach(key => {
                const val = current[key];
                if (typeof val === 'string' && val.toLowerCase().includes(instead)) current[key] = change.use;
                if (Array.isArray(val)) current[key] = val.map(item => (String(item).toLowerCase().includes(instead) ? change.use : item));
            });
            if (!current.storage && (instead.includes('storage') || String(change.use).toLowerCase().includes('storage') || String(change.use).toLowerCase().includes('r2') || String(change.use).toLowerCase().includes('s3'))) current.storage = change.use;
            if (!current.cdn && (instead.includes('cdn') || String(change.use).toLowerCase().includes('cdn') || String(change.use).toLowerCase().includes('cloudflare'))) current.cdn = change.use;
        });
        return current;
    };

    const enrichSupplementals = (stackInput) => {
        const current = { ...stackInput };
        const refined = validation?.refinedArchitecture || {};
        const improved = validation?.improvedArchitecture || {};
        const pick = (key) => current[key] || refined.techStack?.[key] || refined[key] || improved.techStack?.[key] || improved[key];
        ['storage', 'cdn', 'analytics', 'monitoring'].forEach(k => { const val = pick(k); if (val && !current[k]) current[k] = val; });
        return current;
    };

    const finalStack = enrichSupplementals(applyAlternatives(baseValidated));
    try {
        // Production mode - debug logging removed
    } catch { }
    const estimatedTime = validation?.improvedArchitecture?.estimatedTimeframe || research?.estimatedTimeframe || '3-4 weeks';
    const finalConfidence0To1 = data?.confidence
        || data?.metadata?.claudeConfidence
        || (validation?.improvedArchitecture?.validatedFreeLimits ? 0.95 : research ? 0.8 : 0.7);
    const confidence = Math.round((finalConfidence0To1 || 0.85) * 100);
    const monthlyValue = validation?.improvedArchitecture?.validatedFreeLimits?.totalMonthlyValue || '$150';
    const freeServicesCount = validation?.improvedArchitecture?.freeServicesUsed || research?.researchedServices?.totalFreeServices || 8;
    const firstUpgrade = validation?.improvedArchitecture?.firstUpgradeTrigger || research?.estimatedCost?.firstPaidUpgrade || 'At scale';
    const upgradeCost = validation?.improvedArchitecture?.upgradeCost || '$25/month';

    // Title truncation logic
    const MAX_TITLE_LENGTH = 100;
    const displayTitle = originalIdea || 'Your App Project';
    const shouldTruncate = displayTitle.length > MAX_TITLE_LENGTH;
    const currentTitle = isExpanded || !shouldTruncate ? displayTitle : `${displayTitle.substring(0, MAX_TITLE_LENGTH)}...`;

    return (
        <div className="space-y-6">
            {/* Hero Section */}
            <div className="glass-panel p-4 sm:p-6 md:p-8 rounded-2xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
                    <div className="mb-4 sm:mb-0">
                        <Heading level={2} className="text-xl sm:text-2xl md:text-3xl mb-2 text-white">
                            <div className="flex items-start">
                                <Icon name="target" size="sm" className="mr-2 sm:mr-3 text-[#7B61FF] mt-1.5 flex-shrink-0" />
                                <span className="break-words">
                                    {currentTitle}
                                    {shouldTruncate && (
                                        <button
                                            onClick={() => setIsExpanded(!isExpanded)}
                                            className="ml-2 text-base font-semibold text-[#7B61FF] hover:text-[#BA87FF] transition-colors inline-block"
                                        >
                                            {isExpanded ? 'Show Less' : 'Read More'}
                                        </button>
                                    )}
                                </span>
                            </div>
                        </Heading>
                        <Text variant="secondary" size="sm" className="sm:text-base md:text-lg text-gray-300">Production-ready architecture validated by AI</Text>
                    </div>
                    <div className="text-left sm:text-right flex-shrink-0">
                        <div className="text-2xl sm:text-3xl font-bold text-[#7B61FF]">{confidence}%</div>
                        <Text variant="accent" size="xs" className="sm:text-sm text-[#7B61FF]/80">Confidence</Text>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-4 sm:mt-6">
                    <div className="bg-white/5 border border-white/5 rounded-xl p-3 sm:p-4 hover:bg-white/10 transition-colors">
                        <Icon name="clock" size="sm" className="mb-1 text-[#7B61FF]" />
                        <Text variant="primary" weight="semibold" size="sm" className="sm:text-base text-white">{estimatedTime}</Text>
                        <Text variant="secondary" size="xs" className="sm:text-sm text-gray-400">Implementation Time</Text>
                    </div>
                    <div className="bg-white/5 border border-white/5 rounded-xl p-3 sm:p-4 hover:bg-white/10 transition-colors">
                        <Icon name="money" size="sm" className="mb-1 text-[#7B61FF]" />
                        <Text variant="primary" weight="semibold" size="sm" className="sm:text-base text-white">$0/month</Text>
                        <Text variant="secondary" size="xs" className="sm:text-sm text-gray-400">Free Tier Optimized</Text>
                    </div>
                    <div className="bg-white/5 border border-white/5 rounded-xl p-3 sm:p-4 sm:col-span-2 lg:col-span-1 hover:bg-white/10 transition-colors">
                        <Icon name="target" size="sm" className="mb-1 text-[#7B61FF]" />
                        <Text variant="primary" weight="semibold" size="sm" className="sm:text-base text-white break-words">{platform || 'Mobile'}</Text>
                        <Text variant="secondary" size="xs" className="sm:text-sm text-gray-400">Target Platform</Text>
                    </div>
                </div>
            </div>

            {/* Solution Description */}
            {(() => {
                const solutionDescription = data?.solutionDescription ||
                    data?.analysis?.solutionDescription ||
                    validation?.improvedArchitecture?.solutionDescription ||
                    research?.solutionDescription;

                return solutionDescription && solutionDescription !== 'AI-generated solution overview' && (
                    <div className="glass-panel p-4 sm:p-6 rounded-2xl">
                        <Heading level={3} className="text-lg sm:text-xl mb-3 sm:mb-4 flex items-center text-white">
                            <Icon name="lightbulb" size="sm" className="mr-2 sm:mr-3 text-[#7B61FF]" />
                            Solution Overview
                        </Heading>
                        <Text variant="primary" size="sm" className="sm:text-base leading-relaxed text-gray-300">
                            {solutionDescription}
                        </Text>
                    </div>
                );
            })()}

            {/* Q&A Section - Show user answers to project questions */}
            {answers && answers.length > 0 && (
                <div className="glass-panel p-4 sm:p-6 rounded-2xl">
                    <Heading level={3} className="text-lg sm:text-xl mb-3 sm:mb-4 flex items-center text-white">
                        <Icon name="clipboard" size="sm" className="mr-2 sm:mr-3 text-white" />
                        Your Project Requirements
                    </Heading>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                        {answers.map((answer, index) => (
                            <div key={index} className="bg-white/5 border border-white/5 rounded-xl p-3 sm:p-4 hover:bg-white/10 transition-colors">
                                <Text variant="primary" weight="semibold" size="sm" className="sm:text-base mb-2 leading-relaxed text-white">
                                    {answer.question}
                                </Text>
                                <div className="flex items-center">
                                    <Icon name="check" size="xs" className="mr-2 text-[#7B61FF] flex-shrink-0" />
                                    <Text variant="primary" size="sm" className="sm:text-base break-words leading-relaxed text-gray-300">
                                        {answer.answer}
                                    </Text>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Core Features */}
            {(() => {
                const coreFeatures = validation?.improvedArchitecture?.coreFeatures ||
                    research?.coreFeatures ||
                    data?.coreFeatures || [];

                return coreFeatures.length > 0 && (
                    <div className="glass-panel p-4 sm:p-6 rounded-2xl">
                        <Heading level={3} className="text-lg sm:text-xl mb-3 sm:mb-4 flex items-center text-white">
                            <Icon name="clipboard" size="sm" className="mr-2 sm:mr-3 text-[#7B61FF]" />
                            Core Features
                        </Heading>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                            {coreFeatures.map((feature, index) => (
                                <div key={index} className="flex items-center p-2 sm:p-3 bg-white/5 border border-white/5 rounded-xl hover:bg-white/10 transition-colors">
                                    <Icon name="check" size="xs" className="mr-2 sm:mr-3 text-[#7B61FF] flex-shrink-0" />
                                    <Text variant="primary" weight="medium" size="sm" className="sm:text-base break-words text-gray-200">{feature}</Text>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}


            {/* Final Validated Stack */}
            <div className="glass-panel p-4 sm:p-6 rounded-2xl">
                <Heading level={3} className="text-lg sm:text-xl mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center text-white">
                    <div className="flex items-center">
                        <Icon name="shield" size="sm" className="mr-2 sm:mr-3 text-[#7B61FF]" />
                        Recommended Architecture
                    </div>
                    <Badge variant="success" size="sm" className="mt-2 sm:mt-0 sm:ml-3 self-start">
                        AI VALIDATED
                    </Badge>
                </Heading>

                {Object.keys(finalStack).length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {Object.entries(finalStack).map(([category, tech]) => (
                            <div key={category} className="bg-white/5 border border-white/5 rounded-xl p-3 sm:p-4 hover:bg-white/10 transition-colors">
                                <Text variant="primary" weight="semibold" size="sm" className="capitalize mb-1 sm:mb-2 sm:text-base text-[#7B61FF]">{category}</Text>
                                <Text variant="secondary" size="xs" className="sm:text-sm leading-relaxed break-words text-gray-300">{Array.isArray(tech) ? tech.join(', ') : (typeof tech === 'object' && tech !== null ? Object.entries(tech).map(([k, v]) => `${k}: ${v}`).join(', ') : tech)}</Text>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-6 sm:py-8">
                        <Icon name="refresh" size="2xl" className="mb-2 mx-auto text-[#7B61FF]" />
                        <Text variant="secondary" size="sm" className="sm:text-base text-gray-400">Architecture recommendations will appear here after analysis</Text>
                    </div>
                )}
            </div>

            {/* Validation Details: Free Limits & Alternatives (moved after Recommended Architecture) */}
            {(() => {
                const freeLimits = validation?.improvedArchitecture?.validatedFreeLimits || validation?.validatedFreeLimits;
                const alternatives = validation?.betterFreeAlternatives;
                if (!freeLimits && (!alternatives || alternatives.length === 0)) return null;
                return (
                    <div className="glass-panel p-4 sm:p-6 rounded-2xl">
                        <Heading level={3} className="text-lg sm:text-xl mb-3 sm:mb-4 flex items-center text-white">
                            <Icon name="shield" size="sm" className="mr-2 sm:mr-3 text-[#7B61FF]" />
                            Validation Details
                        </Heading>
                        {freeLimits && (
                            <div className="mb-4">
                                <Text variant="primary" weight="semibold" size="sm" className="sm:text-base text-gray-200">Validated Free Limits</Text>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                    {Object.entries(freeLimits.breakdown || {}).map(([k, v]) => (
                                        <div key={k} className="bg-white/5 border border-white/5 rounded-lg p-2">
                                            <Text variant="accent" size="xs" className="capitalize text-[#7B61FF]">{k}</Text>
                                            <Text variant="secondary" size="xs" className="text-gray-300">{String(v)}</Text>
                                        </div>
                                    ))}
                                </div>
                                {freeLimits.totalMonthlyValue && (
                                    <Text variant="secondary" size="xs" className="mt-2 block text-green-400">Estimated Monthly Value: {freeLimits.totalMonthlyValue}</Text>
                                )}
                            </div>
                        )}
                        {alternatives && alternatives.length > 0 && (
                            <div>
                                <Text variant="primary" weight="semibold" size="sm" className="sm:text-base text-gray-200">Alternatives Applied</Text>
                                <div className="mt-2 space-y-2">
                                    {alternatives.map((alt, idx) => (
                                        <div key={idx} className="bg-white/5 border border-white/5 rounded-lg p-2">
                                            <Text variant="accent" size="xs" className="text-[#7B61FF]">{alt.use}</Text>
                                            <Text variant="secondary" size="xs" className="block text-gray-300">instead of {alt["instead-of"]}</Text>
                                            {alt.reason && <Text variant="muted" size="xs" className="block text-gray-500">Reason: {alt.reason}</Text>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Challenges & Considerations */}
            {(() => {
                const challenges = validation?.validation?.concerns ||
                    data?.challenges ||
                    research?.challenges || [];

                return challenges.length > 0 && (
                    <div className="glass-panel p-4 sm:p-6 rounded-2xl">
                        <Heading level={3} className="text-lg sm:text-xl mb-3 sm:mb-4 flex items-center text-white">
                            <Icon name="warning" size="sm" className="mr-2 sm:mr-3 text-[#FFB800]" />
                            Challenges & Considerations
                        </Heading>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                            {challenges.map((challenge, index) => (
                                <div key={index} className="flex items-start p-3 sm:p-4 bg-[#FFB800]/10 border border-[#FFB800]/20 rounded-lg hover:bg-[#FFB800]/20 transition-colors">
                                    <Icon name="warning" size="xs" className="mr-2 sm:mr-3 text-[#FFB800] flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <Text variant="primary" weight="medium" size="sm" className="block break-words sm:text-base text-gray-200">
                                            {typeof challenge === 'string' ? challenge : challenge.title || challenge.concern || challenge}
                                        </Text>
                                        {typeof challenge === 'object' && challenge.description && (
                                            <Text variant="secondary" size="xs" className="sm:text-sm mt-1 break-words leading-relaxed text-gray-400">
                                                {challenge.description}
                                            </Text>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Project Metrics */}
                <div className="glass-panel p-4 sm:p-6 rounded-2xl">
                    <Heading level={3} className="text-base sm:text-lg mb-3 sm:mb-4 flex items-center text-white">
                        <Icon name="chart" size="sm" className="mr-2 text-[#7B61FF]" />
                        Project Metrics
                    </Heading>
                    <div className="space-y-3 sm:space-y-4">
                        <div className="flex justify-between items-center">
                            <Text variant="secondary" size="sm" className="sm:text-base text-gray-400">Free Services Used</Text>
                            <Text variant="primary" weight="semibold" size="sm" className="sm:text-base text-white">{freeServicesCount} services</Text>
                        </div>
                        <div className="flex justify-between items-center">
                            <Text variant="secondary" size="sm" className="sm:text-base text-gray-400">Monthly Value</Text>
                            <Text weight="semibold" size="sm" className="sm:text-base text-green-400">{monthlyValue} (free)</Text>
                        </div>
                        <div className="flex justify-between items-center">
                            <Text variant="secondary" size="sm" className="sm:text-base text-gray-400">First Upgrade</Text>
                            <Text variant="primary" weight="semibold" size="sm" className="sm:text-base break-words text-right text-white">{firstUpgrade}</Text>
                        </div>
                        <div className="flex justify-between items-center">
                            <Text variant="secondary" size="sm" className="sm:text-base text-gray-400">Upgrade Cost</Text>
                            <Text variant="primary" weight="semibold" size="sm" className="sm:text-base text-white">{upgradeCost}</Text>
                        </div>
                    </div>
                </div>

                {/* Implementation Readiness */}
                <div className="glass-panel p-4 sm:p-6 rounded-2xl">
                    <Heading level={3} className="text-base sm:text-lg mb-3 sm:mb-4 flex items-center text-white">
                        <Icon name="check" size="sm" className="mr-2 text-[#7B61FF]" />
                        Implementation Status
                    </Heading>
                    <div className="space-y-2 sm:space-y-3">
                        <div className="flex items-center">
                            <Icon name="check" size="xs" className="mr-2 sm:mr-3 text-green-400 flex-shrink-0" />
                            <Text variant="secondary" size="sm" className="sm:text-base text-gray-300">Architecture validated</Text>
                        </div>
                        <div className="flex items-center">
                            <Icon name="check" size="xs" className="mr-2 sm:mr-3 text-green-400 flex-shrink-0" />
                            <Text variant="secondary" size="sm" className="sm:text-base text-gray-300">Free services selected</Text>
                        </div>
                        <div className="flex items-center">
                            <Icon name="check" size="xs" className="mr-2 sm:mr-3 text-green-400 flex-shrink-0" />
                            <Text variant="secondary" size="sm" className="sm:text-base text-gray-300">Tech stack optimized</Text>
                        </div>
                        <div className="flex items-center">
                            <Icon name="arrowUp" size="xs" className="mr-2 sm:mr-3 text-[#7B61FF] flex-shrink-0" />
                            <Text variant="secondary" size="sm" className="sm:text-base text-gray-300">Ready for implementation</Text>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="glass-panel p-4 sm:p-6 rounded-2xl">
                <Heading level={3} className="text-base sm:text-lg mb-3 sm:mb-4 flex items-center text-white">
                    <Icon name="rocket" size="sm" className="mr-2 text-[#7B61FF]" />
                    Next Steps
                </Heading>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <Button
                        onClick={() => onNavigateToTab && onNavigateToTab('research')}
                        variant="secondary"
                        className="flex items-center p-3 sm:p-4 text-left min-w-0 bg-white/5 hover:bg-white/10 border-white/5 text-gray-200"
                    >
                        <Icon name="refresh" size="sm" className="mr-2 sm:mr-3 flex-shrink-0 text-[#7B61FF]" />
                        <div className="min-w-0">
                            <Text variant="primary" weight="medium" size="sm" className="sm:text-base text-white">View Evolution</Text>
                            <Text variant="secondary" size="xs" className="sm:text-sm break-words text-gray-400">See AI improvement process</Text>
                        </div>
                    </Button>
                    <Button
                        onClick={() => onNavigateToTab && onNavigateToTab('technical')}
                        variant="secondary"
                        className="flex items-center p-3 sm:p-4 text-left min-w-0 bg-white/5 hover:bg-white/10 border-white/5 text-gray-200"
                    >
                        <Icon name="settings" size="sm" className="mr-2 sm:mr-3 flex-shrink-0 text-[#7B61FF]" />
                        <div className="min-w-0">
                            <Text variant="primary" weight="medium" size="sm" className="sm:text-base text-white">Technical Details</Text>
                            <Text variant="secondary" size="xs" className="sm:text-sm break-words text-gray-400">Implementation guide</Text>
                        </div>
                    </Button>
                    <Button
                        onClick={() => onNavigateToTab && onNavigateToTab('prompts')}
                        variant="primary"
                        className="flex items-center p-3 sm:p-4 text-left min-w-0 sm:col-span-2 lg:col-span-1 bg-white text-black hover:bg-white/90"
                    >
                        <Icon name="rocket" size="sm" className="mr-2 sm:mr-3 flex-shrink-0 !text-black" />
                        <div className="min-w-0">
                            <Text size="sm" weight="medium" className="sm:text-base !text-black">AI Prompts</Text>
                            <Text size="xs" className="sm:text-sm break-words !text-black/70">Start building now</Text>
                        </div>
                    </Button>
                </div>
            </div>
        </div>
    );
};

// NEW: Progressive Stack Evolution Component - Shows Research → Validation → Final Stack timeline
const ProgressiveStackEvolution = ({ research, validation, finalStack }) => {
    const [activeStep, setActiveStep] = useState(3); // Default to showing final result

    if (!research && !validation) return null;

    // Extract tech stacks from each step (prefer refined over improved)
    const initialStack = research?.techStack || {};
    const refinedStack = validation?.refinedArchitecture?.techStack || {};
    const improvedStack = validation?.improvedArchitecture?.techStack || {};
    const validatedBase = Object.keys(refinedStack).length > 0 ? refinedStack : improvedStack;
    // FIX: betterFreeAlternatives is at validation.betterFreeAlternatives (not validation.validation.betterFreeAlternatives)
    const overlayAlternatives = (stackInput) => {
        const alt = validation?.betterFreeAlternatives;
        if (!alt || !Array.isArray(alt) || alt.length === 0) return stackInput;
        const current = { ...stackInput };
        alt.forEach(change => {
            if (!change || !change["instead-of"] || !change.use) return;
            const instead = String(change["instead-of"]).toLowerCase();
            // Replace values that mention the old provider
            Object.keys(current).forEach(key => {
                const val = current[key];
                if (typeof val === 'string' && val.toLowerCase().includes(instead)) {
                    current[key] = change.use;
                }
                if (Array.isArray(val)) {
                    current[key] = val.map(item => (String(item).toLowerCase().includes(instead) ? change.use : item));
                }
            });
            // Fill missing supplemental categories (e.g., storage, cdn)
            if (!current.storage && (instead.includes('storage') || String(change.use).toLowerCase().includes('storage') || String(change.use).toLowerCase().includes('r2') || String(change.use).toLowerCase().includes('s3'))) {
                current.storage = change.use;
            }
            if (!current.cdn && (instead.includes('cdn') || String(change.use).toLowerCase().includes('cdn') || String(change.use).toLowerCase().includes('cloudflare'))) {
                current.cdn = change.use;
            }
        });
        return current;
    };

    // Apply alternatives to both refined and improved candidates so replacements (e.g., Firebase -> Supabase) are reflected
    const isNonEmpty = (obj) => obj && typeof obj === 'object' && Object.keys(obj).length > 0;
    const correctedRefined = overlayAlternatives(refinedStack);
    const correctedImproved = overlayAlternatives(improvedStack);
    const validatedWithOverlay = overlayAlternatives(isNonEmpty(validatedBase) ? validatedBase : initialStack);
    // Enrich with supplemental keys from validation structures if missing
    const enrichWithSupplementals = (stackInput) => {
        const current = { ...stackInput };
        const refined = validation?.refinedArchitecture || {};
        const improved = validation?.improvedArchitecture || {};
        const refTS = refined.techStack || {};
        const impTS = improved.techStack || {};
        const pick = (key) => current[key] || refTS[key] || refined[key] || impTS[key] || improved[key];
        ['storage', 'cdn', 'analytics', 'monitoring'].forEach(k => {
            const val = pick(k);
            if (val && !current[k]) current[k] = val;
        });
        return current;
    };

    const pickFirstNonEmpty = (...cands) => {
        for (const c of cands) {
            if (isNonEmpty(c)) return c;
        }
        return {};
    };

    // Final candidate: prefer refined → improved; apply alternatives; then enrich with supplemental keys
    const baseValidated = isNonEmpty(refinedStack) ? refinedStack : (isNonEmpty(improvedStack) ? improvedStack : initialStack);
    const finalTechStack = enrichWithSupplementals(overlayAlternatives(baseValidated));

    try {
        // Production mode - debug logging removed
    } catch { }

    // Step data
    const steps = [
        {
            id: 1,
            title: "Initial Research",
            status: "completed",
            icon: "search",
            description: "AI discovers services and evaluates free tiers",
            techStack: initialStack,
            reasoning: research?.reasoning || "Research-driven service discovery",
            confidence: 0.7
        },
        {
            id: 2,
            title: "Validation Review",
            status: "completed",
            icon: "check",
            description: "Architecture review finds better alternatives",
            techStack: validatedWithOverlay,
            reasoning: validation?.recommendations || "Architecture optimization",
            improvements: validation?.betterFreeAlternatives || [],
            confidence: 0.85
        },
        {
            id: 3,
            title: "Final Stack",
            status: "active",
            icon: "target",
            description: "Optimized architecture ready for implementation",
            techStack: finalTechStack,
            reasoning: validation?.improvedArchitecture?.reasoning || "Final validated architecture",
            confidence: 0.95
        }
    ];

    return (

        <div className="glass-panel p-4 sm:p-6 rounded-2xl">
            <Heading level={3} className="text-base sm:text-lg mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center text-white">
                <div className="flex items-center">
                    <Icon name="refresh" size="sm" className="mr-2 text-white" />
                    Technology Stack Evolution
                </div>
                <Text variant="secondary" size="xs" className="mt-1 sm:mt-0 sm:ml-auto sm:text-sm text-gray-400">Research → Validation → Final</Text>
            </Heading>
            <Text variant="secondary" size="xs" className="sm:text-sm mb-4 sm:mb-6 flex items-center text-gray-300">
                <Icon name="lightbulb" size="xs" className="mr-2 text-white" />
                Click the timeline steps below to explore each stage of the AI improvement process
            </Text>

            {/* Timeline Header - Mobile Responsive */}
            <div className="mb-6 sm:mb-8">
                {/* Mobile: Vertical Timeline */}
                <div className="sm:hidden space-y-3">
                    {steps.map((step, index) => (
                        <div key={step.id} className="flex items-center">
                            <Button
                                onClick={() => setActiveStep(step.id)}
                                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200 transform flex-shrink-0 ${activeStep === step.id
                                    ? 'bg-white text-black border-white shadow-md'
                                    : step.status === 'completed'
                                        ? 'bg-green-500/20 border-green-400 text-green-400'
                                        : 'bg-white/5 border-white/10 text-gray-500'
                                    }`}
                                variant="ghost"
                                size="sm"
                            >
                                <Icon name={step.icon} size="xs" />
                            </Button>
                            <div className="ml-3 flex-1">
                                <Text variant={activeStep === step.id ? "primary" : "secondary"} weight="medium" size="sm" className={activeStep === step.id ? "text-white" : "text-gray-400"}>
                                    {step.title}
                                </Text>
                                <Text variant="secondary" size="xs" className="break-words text-gray-500">{step.description}</Text>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Desktop: Horizontal Timeline */}
                <div className="flex flex-col sm:flex-row items-center justify-between relative px-2 sm:px-4 space-y-4 sm:space-y-0">
                    {steps.map((step, index) => (
                        <div key={step.id} className="flex-1 flex items-center relative z-10 w-full sm:w-auto">
                            <Button
                                onClick={() => setActiveStep(step.id)}
                                className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-300 ${activeStep === step.id
                                    ? 'bg-[#7B61FF] border-[#7B61FF] text-white shadow-[0_0_15px_rgba(123,97,255,0.4)] scale-110'
                                    : activeStep > step.id
                                        ? 'bg-[#7B61FF]/20 border-[#7B61FF]/50 text-[#7B61FF]'
                                        : 'bg-white/5 border-white/10 text-gray-500 hover:border-white/20'
                                    }`}
                            >
                                {activeStep > step.id ? <Icon name="check" size="xs" /> : step.id}
                            </Button>
                            <div className="ml-3 flex-1 min-w-0">
                                <Text
                                    variant={activeStep === step.id ? "primary" : "secondary"}
                                    weight="medium"
                                    size="sm"
                                    className={`truncate ${activeStep === step.id ? "text-white" : "text-gray-400"}`}
                                >
                                    {step.title}
                                </Text>
                                <Text variant="secondary" size="xs" className="text-gray-500 truncate hidden sm:block">{step.description}</Text>
                            </div>
                            {index < steps.length - 1 && (
                                <div className="hidden sm:block flex-1 h-0.5 bg-white/10 mx-4"></div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Active Step Content */}
            {steps.map(step => (
                activeStep === step.id && (
                    <div key={step.id} className="space-y-6 animate-fade-in">
                        {/* Step Info */}
                        <div className="bg-white/5 border border-white/5 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                                <Heading level={4} className="flex items-center text-white">
                                    <Icon name={step.icon} size="xs" className="mr-2 text-[#7B61FF]" />
                                    {step.title}
                                </Heading>
                                <Badge variant="secondary" size="sm" className="bg-white/10 text-gray-300">
                                    {Math.round(step.confidence * 100)}% Confidence
                                </Badge>
                            </div>
                            <Text variant="secondary" size="sm" className="text-gray-400">{step.reasoning}</Text>
                        </div>

                        {/* Improvements (Step 2 only) */}
                        {step.id === 2 && step.improvements && step.improvements.length > 0 && (
                            <div className="bg-white/5 border border-white/5 rounded-xl p-4">
                                <Heading level={5} className="mb-3 flex items-center text-white">
                                    <Icon name="lightbulb" size="xs" className="mr-2 text-[#7B61FF]" />
                                    Improvements Found
                                </Heading>
                                <div className="space-y-2">
                                    {step.improvements.map((improvement, idx) => (
                                        <div key={idx} className="flex items-start">
                                            <Icon name="check" size="xs" className="mr-2 text-green-400 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm">
                                                <Text variant="primary" weight="semibold" size="sm" className="text-gray-200">
                                                    {improvement.use || 'Improvement'}
                                                </Text>
                                                {improvement['instead-of'] && (
                                                    <Text variant="secondary" size="sm" className="inline text-gray-400"> instead of {improvement['instead-of']}</Text>
                                                )}
                                                {improvement.reason && (
                                                    <div className="mt-1">
                                                        <Text variant="secondary" size="xs" className="text-gray-500">
                                                            Reason: {improvement.reason}
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Tech Stack Display */}
                        {step.techStack && Object.keys(step.techStack).length > 0 && (
                            <div className="bg-white/5 border border-white/5 rounded-xl p-4">
                                <Heading level={5} className="mb-3 flex items-center text-white">
                                    <Icon name="settings" size="xs" className="mr-2 text-white" />
                                    Technology Stack
                                    {step.id === 3 && (
                                        <Badge variant="success" size="sm" className="ml-2">
                                            RECOMMENDED
                                        </Badge>
                                    )}
                                </Heading>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {Object.entries(step.techStack).map(([category, tech]) => (
                                        <div key={category} className="bg-black/20 border border-white/5 p-3 rounded-lg hover:bg-black/40 transition-colors">
                                            <Text variant="primary" weight="medium" size="sm" className="capitalize text-[#7B61FF]">
                                                {category}
                                            </Text>
                                            <Text variant="secondary" size="sm" className="mt-1 text-gray-300">
                                                {Array.isArray(tech) ? tech.join(', ') : (typeof tech === 'object' && tech !== null ? Object.entries(tech).map(([k, v]) => `${k}: ${v}`).join(', ') : tech)}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Next Step Indicator */}
                        {step.id < 3 && (
                            <div className="text-center pt-4">
                                <Button
                                    onClick={() => setActiveStep(step.id + 1)}
                                    variant="ghost"
                                    size="sm"
                                    className="inline-flex items-center text-[#7B61FF] hover:bg-white/5"
                                >
                                    View Next Step
                                    <Icon name="arrowRight" size="xs" className="ml-1" />
                                </Button>
                            </div>
                        )}
                    </div>
                )
            ))}
        </div>
    );
};

export default AnalysisDisplay;

// Export research components for use in separate Research tab
export {
    ComprehensiveResearchSection,
    ValidationResultsSection,
    ConfidenceDashboardSection,
    CostAnalysisSection,
    ProgressiveStackEvolution,
    ExecutiveSummaryOverview
}; 